import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { fetchGig, fetchReviews, createOrder, sendMessage, fetchRelated, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFav } from "@/context/FavContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Star, Clock, RefreshCcw, Check, MessageSquare, ArrowRight, Heart } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import GigCard from "@/components/GigCard";
import ReviewsBlock from "@/components/ReviewsBlock";
import PaymentChooser from "@/components/PaymentChooser";
import VerifiedBadge from "@/components/VerifiedBadge";
import SEO from "@/components/SEO";

export default function GigDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { isFav, toggle } = useFav();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [gig, setGig] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [related, setRelated] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState("Basic");
  const [activeImg, setActiveImg] = useState(0);
  const [requirements, setRequirements] = useState("");
  const [orderOpen, setOrderOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [msgOpen, setMsgOpen] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [pendingPrice, setPendingPrice] = useState(0);

  useEffect(() => {
    fetchGig(id).then(setGig).catch(() => setGig(false));
    fetchReviews({ gig_id: id }).then(setReviews).catch(() => {});
    fetchRelated(id).then(setRelated).catch(() => {});
  }, [id]);

  if (gig === false) return <div className="p-16 text-center">Gig not found.</div>;
  if (!gig) return <div className="p-16 text-center">Loading…</div>;

  const pkg = gig.packages.find((p) => p.name === selectedPkg) || gig.packages[0];
  const images = gig.images?.length ? gig.images : [gig.cover_image];
  const ytId = (() => {
    if (!gig.video_url) return null;
    try {
      const u = new URL(gig.video_url.startsWith("http") ? gig.video_url : "https://" + gig.video_url);
      if (u.hostname.includes("youtu.be")) return u.pathname.replace("/", "") || null;
      if (u.hostname.includes("youtube.com")) {
        if (u.pathname === "/watch") return u.searchParams.get("v");
        if (u.pathname.startsWith("/embed/") || u.pathname.startsWith("/shorts/") || u.pathname.startsWith("/live/")) return u.pathname.split("/")[2];
      }
    } catch {}
    return null;
  })();

  const handleOrder = async () => {
    if (!user) {
      toast.error("Please sign in first");
      navigate("/login");
      return;
    }
    try {
      const order = await createOrder({ gig_id: gig.gig_id, package_name: selectedPkg, requirements });
      setPendingOrderId(order.order_id);
      setPendingPrice(order.package_price);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order failed");
    }
  };

  const handleContact = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      await sendMessage({ recipient_id: gig.seller_id, text: msgText });
      toast.success("Message sent");
      setMsgOpen(false);
      setMsgText("");
      navigate(`/messages/${gig.seller_id}`);
    } catch (e) {
      toast.error("Failed to send");
    }
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8">
      <SEO
        title={gig.title}
        description={(gig.description || "").slice(0, 160)}
        image={gig.cover_image ? fileUrl(gig.cover_image) : undefined}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: gig.title,
          description: (gig.description || "").slice(0, 400),
          image: (gig.images || []).map((i) => fileUrl(i)).filter(Boolean),
          brand: { "@type": "Brand", name: "Noxi" },
          offers: {
            "@type": "Offer",
            price: Math.min(...(gig.packages || []).map((p) => p.price)),
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: typeof window !== "undefined" ? window.location.href : "",
          },
          aggregateRating: gig.rating_count > 0 ? {
            "@type": "AggregateRating",
            ratingValue: gig.rating_avg,
            reviewCount: gig.rating_count,
          } : undefined,
        }}
      />
      <div className="overline text-muted-foreground mb-3">
        <Link to="/browse" className="hover:text-primary">EXPLORE</Link> / <span className="text-foreground">{gig.category?.toUpperCase()}</span>
      </div>

      <div className="grid lg:grid-cols-[1fr_420px] gap-10">
        {/* LEFT */}
        <div>
          <div className="flex items-start justify-between gap-4 mb-6">
            <h1 data-testid="gig-title" className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              {gig.title}
            </h1>
            <button
              data-testid="gig-fav-btn"
              onClick={async () => {
                if (!user) { navigate("/login"); return; }
                const r = await toggle(gig.gig_id);
                if (r?.favorited) toast.success("Saved to favorites");
              }}
              className={`flex-shrink-0 border p-3 transition-all ${isFav(gig.gig_id) ? "bg-primary border-primary text-primary-foreground" : "border-foreground/20 hover:border-foreground"}`}
              aria-label="Save to favorites"
            >
              <Heart className={`w-5 h-5 ${isFav(gig.gig_id) ? "fill-white" : ""}`} strokeWidth={1.5}/>
            </button>
          </div>

          <Link to={`/seller/${gig.seller_id}`} data-testid="gig-seller-link" className="inline-flex items-center gap-3 mb-6 group">
            {gig.seller_picture ? (
              <img src={gig.seller_picture} className="w-11 h-11 rounded-full object-cover border-2 border-foreground" alt={gig.seller_name}/>
            ) : (
              <div className="w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                {gig.seller_name?.[0]}
              </div>
            )}
            <div>
              <div className="font-medium group-hover:text-primary transition-colors flex items-center gap-1.5">
                {gig.seller_name}
                <VerifiedBadge verified={gig.seller_verified}/>
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Star className="w-3.5 h-3.5 fill-foreground text-foreground"/>
                <span className="text-foreground font-medium">{gig.rating_avg?.toFixed(1) || "New"}</span>
                <span>({gig.rating_count} reviews)</span>
              </div>
            </div>
          </Link>

          {/* Gallery */}
          <div className="border border-foreground/15 mb-8">
            <div className="aspect-video bg-muted overflow-hidden">
              {activeImg === "video" && ytId ? (
                <iframe
                  data-testid="gig-video-embed"
                  src={`https://www.youtube.com/embed/${ytId}`}
                  title="Gig video"
                  className="w-full h-full"
                  frameBorder="0"
                  allowFullScreen
                />
              ) : (
                <img src={fileUrl(images[activeImg] === undefined ? images[0] : images[activeImg])} alt="" className="w-full h-full object-cover"/>
              )}
            </div>
            {(images.length > 1 || ytId) && (
              <div className="flex gap-2 p-2 overflow-x-auto">
                {ytId && (
                  <button
                    onClick={() => setActiveImg("video")}
                    data-testid="gig-thumb-video"
                    className={`w-32 aspect-video flex-shrink-0 border relative ${activeImg === "video" ? "border-primary border-2" : "border-transparent"}`}
                  >
                    <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} className="w-full h-full object-cover" alt="video thumb"/>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                  </button>
                )}
                {images.map((im, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`gig-thumb-${i}`}
                    className={`w-32 aspect-video flex-shrink-0 border ${activeImg === i ? "border-primary border-2" : "border-transparent"}`}
                  >
                    <img src={fileUrl(im)} className="w-full h-full object-cover" alt=""/>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <Tabs defaultValue="about">
            <TabsList className="rounded-none border border-foreground/15 bg-transparent p-0 h-auto">
              <TabsTrigger data-testid="tab-about" value="about" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-6 py-3">About this gig</TabsTrigger>
              <TabsTrigger data-testid="tab-reviews" value="reviews" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-6 py-3">Reviews ({reviews.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="about" className="pt-6">
              <div className="prose prose-sm max-w-none whitespace-pre-wrap leading-relaxed text-foreground/90" data-testid="gig-description">
                {gig.description}
              </div>
              {gig.tags?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {gig.tags.map((t) => (
                    <span key={t} className="text-xs mono border border-foreground/20 px-2 py-1 uppercase tracking-wider">{t}</span>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="reviews" className="pt-6">
              <ReviewsBlock gigId={gig.gig_id} sellerId={gig.seller_id}/>
            </TabsContent>
          </Tabs>
        </div>

        {/* RIGHT - packages */}
        <div className="lg:sticky lg:top-24 self-start">
          <div className="border border-foreground brutal-shadow bg-card">
            <div className="grid grid-cols-3 border-b border-foreground/15">
              {gig.packages.map((p) => (
                <button
                  key={p.name}
                  data-testid={`pkg-tab-${p.name}`}
                  onClick={() => setSelectedPkg(p.name)}
                  className={`py-3 text-xs mono font-medium border-r last:border-r-0 border-foreground/15 transition-colors ${selectedPkg === p.name ? "bg-foreground text-background" : "hover:bg-muted"}`}
                >
                  {p.name.toUpperCase()}
                </button>
              ))}
            </div>
            <div className="p-6 space-y-5">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="font-semibold text-lg">{pkg.title}</div>
                </div>
                <div className="mono text-3xl font-bold" data-testid="pkg-price">{format(pkg.price, { decimals: 0 })}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 border-y border-foreground/10 py-4">
                <div className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4" strokeWidth={1.5}/> {pkg.delivery_days}-day delivery</div>
                <div className="flex items-center gap-2 text-sm"><RefreshCcw className="w-4 h-4" strokeWidth={1.5}/> {pkg.revisions} revisions</div>
              </div>
              <ul className="space-y-2">
                {pkg.features?.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" strokeWidth={2}/> {f}
                  </li>
                ))}
              </ul>

              {gig.paused && (
                <div data-testid="gig-paused-notice" className="border border-foreground/20 bg-muted px-4 py-3 text-sm">
                  This gig is paused by the seller and isn't taking new orders right now. You can still message the seller.
                </div>
              )}
              <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
                <DialogTrigger asChild disabled={gig.paused}>
                  <button
                    data-testid="continue-order-btn"
                    disabled={gig.paused}
                    className="disabled:opacity-40 disabled:cursor-not-allowed w-full bg-primary text-primary-foreground py-3.5 mono text-sm font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 group"
                  >
                    CONTINUE ({format(pkg.price, { decimals: 0 })}) <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
                  </button>
                </DialogTrigger>
                <DialogContent className="rounded-none">
                  <DialogHeader><DialogTitle>{pendingOrderId ? "Complete payment" : "Place your order"}</DialogTitle></DialogHeader>
                  {!pendingOrderId ? (
                    <>
                      <div className="space-y-3">
                        <div className="text-sm text-muted-foreground">You're ordering the <b>{pkg.name}</b> package ({format(pkg.price, { decimals: 0 })}) — {pkg.delivery_days} days delivery.</div>
                        <div>
                          <label className="overline block mb-2">◆ Requirements (optional)</label>
                          <Textarea
                            data-testid="order-requirements"
                            value={requirements}
                            onChange={(e) => setRequirements(e.target.value)}
                            placeholder="Anything the seller needs to know?"
                            className="rounded-none border-foreground/20"
                            rows={5}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <button
                          data-testid="place-order-btn"
                          onClick={handleOrder}
                          className="bg-primary text-primary-foreground px-6 py-2.5 mono text-sm hover:bg-primary/90"
                        >
                          CONTINUE TO PAYMENT →
                        </button>
                      </DialogFooter>
                    </>
                  ) : (
                    <PaymentChooser orderId={pendingOrderId} priceUsd={pendingPrice}/>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
                <DialogTrigger asChild>
                  <button
                    data-testid="contact-seller-btn"
                    className="w-full border border-foreground py-3 mono text-sm hover:bg-foreground hover:text-background transition-colors flex items-center justify-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" strokeWidth={1.5}/> CONTACT SELLER
                  </button>
                </DialogTrigger>
                <DialogContent className="rounded-none">
                  <DialogHeader><DialogTitle>Message {gig.seller_name}</DialogTitle></DialogHeader>
                  <Textarea
                    data-testid="contact-message-text"
                    value={msgText}
                    onChange={(e) => setMsgText(e.target.value)}
                    placeholder="Hi, I'd like to discuss..."
                    className="rounded-none"
                    rows={5}
                  />
                  <DialogFooter>
                    <button
                      data-testid="send-message-btn"
                      onClick={handleContact}
                      disabled={!msgText.trim()}
                      className="bg-foreground text-background px-6 py-2.5 mono text-sm disabled:opacity-40"
                    >SEND</button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      {/* RELATED GIGS */}
      {related.length > 0 && (
        <section className="mt-20 border-t border-black/10 pt-12">
          <div className="overline text-primary mb-2">◆ YOU MIGHT ALSO LIKE</div>
          <h2 className="text-3xl font-bold tracking-tight mb-8">Related gigs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.slice(0, 4).map((g, i) => (
              <GigCard key={g.gig_id} gig={g} testId={`related-gig-${i}`}/>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
